import React from 'react';
import { describeError, getPageRef, logger } from 'utils/logger';
import { captureExceptionInLogRocket } from 'utils/logrocket';

interface State {
  hasError: boolean;
}

// Records React render crashes (otherwise the page just goes blank) and
// shows the user a reference they can send to support.
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureExceptionInLogRocket(error, { componentStack: info?.componentStack?.slice(0, 2000) });
    logger.error('react_render_error', {
      error: describeError(error),
      componentStack: info?.componentStack?.slice(0, 2000),
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-center px-4">
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">Something went wrong</h1>
        <p className="text-gray-600 mb-6">Please reload the page and try again.</p>
        <button
          className="px-8 py-3 bg-red-700 text-white font-semibold rounded-lg shadow-md"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
        <p className="text-xs text-gray-400 mt-6">Ref: {getPageRef()}</p>
      </div>
    );
  }
}

export default ErrorBoundary;
